import React, { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { usePinProtection } from '@/contexts';
import { RouteSkeleton } from '@/components/Router/routeSkeletons';

const PROTECTED_ROUTES = ['/actividad', '/clientes', '/analytics', '/ajustes/medios-de-cobro', '/caja'];

// Pantallas con secciones en la URL (`/analytics/equipo`, `/analytics/agenda`…): moverse entre
// sus secciones es seguir en la misma pantalla, así que no vuelve a pedir el PIN. Por
// segmento: `/actividad-landing` no es una sección de `/actividad`.
const SECTIONED_ROUTES = ['/analytics', '/actividad'];
const inSection = (path: string, root: string) => path === root || path.startsWith(`${root}/`);
const sameSectionedScreen = (a: string, b: string) =>
  SECTIONED_ROUTES.some((root) => inSection(a, root) && inSection(b, root));

interface PinProtectedRouteProps {
  children: React.ReactNode;
}

export const PinProtectedRoute: React.FC<PinProtectedRouteProps> = ({ children }) => {
  const { pathname, search, hash } = useLocation();
  const {
    isPinConfigured,
    isPinEnabled,
    isVerified,
    isLoading,
    setShowPinModal,
    setPendingRoute,
    resetVerification
  } = usePinProtection();

  const previousPathRef = useRef<string | null>(null);

  // Check if current route needs PIN protection
  const isProtectedRoute = PROTECTED_ROUTES.some((route) => pathname.startsWith(route));
  const needsVerification = isProtectedRoute && isPinConfigured && isPinEnabled && !isVerified && !isLoading;
  // Todavía no se sabe si hay PIN: no pintar el contenido para no tener que sacarlo.
  const statusPending = isProtectedRoute && isLoading;

  useEffect(() => {
    // Skip if still loading PIN status
    if (isLoading) return;

    // Reset verification when navigating to a DIFFERENT protected route
    if (
      isProtectedRoute &&
      previousPathRef.current &&
      previousPathRef.current !== pathname &&
      !sameSectionedScreen(previousPathRef.current, pathname)
    ) {
      // Only reset if coming from another protected route or non-protected route
      const wasProtected = PROTECTED_ROUTES.some((route) => previousPathRef.current?.startsWith(route));
      if (wasProtected || !previousPathRef.current.startsWith(pathname)) {
        resetVerification();
      }
    }

    previousPathRef.current = pathname;
  }, [pathname, isProtectedRoute, resetVerification, isLoading]);

  useEffect(() => {
    // Show PIN modal when verification is needed
    if (needsVerification) {
      setPendingRoute(`${pathname}${search}${hash}`);
      setShowPinModal(true);
    }
  }, [needsVerification, pathname, search, hash, setPendingRoute, setShowPinModal]);

  // Sin contenido hasta verificar (o hasta saber si hace falta): el esqueleto de la
  // ruta en lugar de `null`, así no hay contenido → blanco → modal. El modal del PIN
  // (PinEntryModal, hermano en el Router) se dibuja encima.
  if (statusPending || needsVerification) {
    return <RouteSkeleton path={pathname} />;
  }

  return children;
};
